import { c as _c } from "react/compiler-runtime";
import { Briefcase, Network, Vote, Wallet, Workflow } from 'lucide-react';
import Image from 'next/image';
import { DecorationDots } from '@/modules/home/components/decoration-dots';
import { OrbitingCircle } from '@/modules/shared/components/orbiting-circle';
import { AchraIsotype } from './achra-isotype';
import { MagicDot } from './magic-dot';
import { PulsingDot } from './pulsing-dot';
import { SurfaceGlow } from './surface-glow';
const images = [{
  src: '/home/build-network/builders/chart.png',
  width: 1116,
  height: 1164
}, {
  src: '/home/build-network/builders/profile.png',
  width: 986,
  height: 468
}, {
  src: '/home/build-network/operators/card.png',
  width: 1024,
  height: 815
}, {
  src: '/home/build-network/orgs/card-bottom-left.png',
  width: 879,
  height: 975
}, {
  src: '/home/build-network/orgs/card-bottom-right.png',
  width: 692,
  height: 698
}, {
  src: '/home/build-network/orgs/card-top-right.png',
  width: 872,
  height: 1234
}, {
  src: '/home/build-network/orgs/network.png',
  width: 890,
  height: 540
}];
function AchraOrbitAnimation() {
  const $ = _c(19);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DecorationDots rows={8} columns={8} dotSize={3} gap={14} fade={{
      direction: "bottom-right",
      from: 0,
      to: 0.4
    }} className="text-primary pointer-events-none absolute top-8 left-10 opacity-70" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DecorationDots rows={9} columns={5} dotSize={3} gap={14} fade={{
      direction: "bottom-left",
      from: 0,
      to: 0.2
    }} className="text-primary pointer-events-none absolute top-12 right-16 hidden opacity-70 md:block" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DecorationDots rows={5} columns={9} dotSize={3} gap={14} fade={{
      direction: "top-right",
      from: 0,
      to: 0.3
    }} className="text-primary pointer-events-none absolute bottom-8 left-10 opacity-70 md:left-32" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DecorationDots rows={5} columns={9} dotSize={3} gap={14} fade={{
      direction: "top-left",
      from: 0,
      to: 0.4
    }} className="text-primary pointer-events-none absolute right-8 bottom-8 opacity-70" />;
    t4 = <MagicDot className="top-10 left-1/4" dotClassName="size-1.5 bg-card" glowClassName="size-5 bg-primary/40" />;
    t5 = <MagicDot className="top-6 left-[42%]" dotClassName="size-1 bg-card" glowClassName="size-4 bg-white/50" />;
    t6 = <MagicDot className="top-14 right-1/4" dotClassName="size-2 bg-card" glowClassName="size-6 bg-primary/40" />;
    t7 = <MagicDot className="top-1/3 right-10" dotClassName="size-1 bg-card" glowClassName="size-4 bg-primary/30" />;
    t8 = <MagicDot className="top-1/2 left-8" dotClassName="size-1.5 bg-card" glowClassName="size-5 bg-primary/40" />;
    t9 = <MagicDot className="top-[58%] right-6" dotClassName="size-2 bg-card" glowClassName="size-6 bg-[rgb(221,80,216)]/40" />;
    t10 = <MagicDot className="bottom-1/3 left-12" dotClassName="size-1 bg-card" glowClassName="size-4 bg-[rgb(221,80,216)]/40" />;
    t11 = <MagicDot className="bottom-16 left-1/3" dotClassName="size-2 bg-card" glowClassName="size-7 bg-[rgb(221,80,216)]/40" />;
    t12 = <MagicDot className="bottom-8 left-1/2" dotClassName="size-1 bg-card" glowClassName="size-4 bg-white/50" />;
    t13 = <MagicDot className="right-1/4 bottom-14" dotClassName="size-2.5 bg-card" glowClassName="size-9 bg-[rgb(221,80,216)]/45" />;
    t14 = <MagicDot className="right-10 bottom-24" dotClassName="size-1.5 bg-card" glowClassName="size-5 bg-primary/40" />;
    $[3] = t10;
    $[4] = t11;
    $[5] = t12;
    $[6] = t13;
    $[7] = t14;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t10 = $[3];
    t11 = $[4];
    t12 = $[5];
    t13 = $[6];
    t14 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="relative flex size-30 items-center justify-center overflow-hidden rounded-full md:size-36 lg:size-40" style={{
      backgroundImage: "\n            radial-gradient(ellipse 65% 60% at 32% 26%, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 60%),\n            radial-gradient(ellipse 100% 100% at 70% 90%, rgba(150, 170, 210, 0.22) 0%, transparent 55%),\n            linear-gradient(155deg, #ffffff 0%, #f4f6fb 60%, #e9edf5 100%)\n          ",
      boxShadow: "\n            0 55px 90px -25px rgba(30, 40, 70, 0.20),\n            0 25px 50px -12px rgba(30, 40, 70, 0.12),\n            0 0 0 1px rgba(255, 255, 255, 0.6),\n            inset 0 -22px 36px rgba(40, 55, 100, 0.06),\n            inset 0 14px 28px rgba(255, 255, 255, 0.95)\n          "
    }}><SurfaceGlow angle={0} className="bg-primary/55" /><SurfaceGlow angle={120} className="bg-[rgb(221,80,216)]/55" /><SurfaceGlow angle={240} className="bg-[rgb(5,130,255)]/55" /><AchraIsotype className="text-primary relative size-14 md:size-18" /></div>;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <OrbitingCircle radiusClassName="[--radius:100] sm:[--radius:120] md:[--radius:130] lg:[--radius:140] xl:[--radius:160]" iconSize={40} duration={20}><PulsingDot dotClassName="bg-primary" waveClassName="bg-primary/45" /><PulsingDot dotClassName="bg-[rgb(221,80,216)]" waveClassName="bg-[rgb(221,80,216)]/45" /><PulsingDot dotClassName="bg-[rgb(5,130,255)]" waveClassName="bg-[rgb(5,130,255)]/45" /></OrbitingCircle>;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="hidden sm:contents"><OrbitingCircle radiusClassName="[--radius:210] lg:[--radius:250] xl:[--radius:290]" iconSize={48} duration={40} reverse={true} className="border-border bg-card text-foreground size-9 border shadow-sm lg:size-12"><Network className="size-4 lg:size-5" aria-label="Networks" /><Workflow className="size-4 lg:size-5" aria-label="Workstreams" /><Briefcase className="size-4 lg:size-5" aria-label="Services" /><Vote className="size-4 lg:size-5" aria-label="Governance" /><Wallet className="size-4 lg:size-5" aria-label="Finances" /></OrbitingCircle></div>;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  let t18;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="relative flex h-150 items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_50%_50%,var(--color-card)_0%,var(--color-card)_10%,color-mix(in_oklab,var(--color-primary)_5%,transparent)_100%)]">{t0}{t1}{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}<OrbitingCircle radiusClassName="[--radius:200] sm:[--radius:300] lg:[--radius:370] xl:[--radius:440]" duration={60} className="border-border size-auto overflow-hidden rounded-xl">{images.map(_temp)}</OrbitingCircle></div>;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  return t18;
}
function _temp(image) {
  return <Image key={image.src} src={image.src} width={image.width} height={image.height} alt="" className="h-24 w-auto md:h-32 lg:h-40 xl:h-44" />;
}
export { AchraOrbitAnimation };